import { useEffect } from "react";
import { useSetAtom } from 'jotai';
import type { Setter } from 'jotai';
import { fetchAuthSession } from "aws-amplify/auth";
import { authSessionAtom, authSessionLoadingAtom } from "../atoms/authAtoms";
import { useSnackbar } from "utils/hooks/useSnackbar";

export const useFetchAuthSession = async () => {
    const setAuthSession = useSetAtom(authSessionAtom);
    const setLoading = useSetAtom(authSessionLoadingAtom);
    const { showError } = useSnackbar();

    useEffect(() => {
        const init = async () => {
            setLoading(true);
            try {
                const session = await fetchAuthSession();
                if (session) {
                    setAuthSession(session);
                } else {
                    // alert() を廃止し、再ログイン導線を含む文言をトースト表示する。
                    showError('認証セッションが見つかりません。再ログインしてください。');
                }
            } catch (error) {
                console.error('認証セッションの取得に失敗しました:', error);
                showError('認証セッションの取得に失敗しました。再ログインしてください。');
            } finally {
                setLoading(false);
            }
        };
        init();
    }, [setAuthSession, setLoading, showError]);
};

// Cognito ID トークンの強制更新（issue #42。ジョブ監視ポーリングが長時間続く間の401対策）。
// useFetchAuthSession の初回取得とは独立した経路で、React コンポーネント外（Jotai write atom の
// クロージャ）からも呼べるよう Setter を直接受け取る形にする。idToken が取得できない場合は
// 呼び出し側（ポーリングループ）がリトライ回数を判断できるよう、ここでは即時に throw する。
export const refreshAuthSession = async (set: Setter): Promise<string> => {
    const session = await fetchAuthSession({ forceRefresh: true });
    set(authSessionAtom, session);
    const token = session?.tokens?.idToken?.toString() ?? "";
    if (!token) throw new Error("refreshAuthSession: idToken missing after forceRefresh");
    return token;
};
